import { useState, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { searchProducts, getLocations } from '../services/api';
import { addSearchHistory } from '../services/storage';
import { ProductListing, LocationResult } from '../types';
import { QueryRelevanceFilter } from '../lib/queryRelevanceFilter';

export function useProductSearch() {
  const { userId } = useAuth();
  const { user } = useUser();
  const userEmail = user?.primaryEmailAddress?.emailAddress ?? undefined;
  const userIdOrUndefined = userId ?? undefined;
  const [listings, setListings] = useState<ProductListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationResult[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const [currentLocation, setCurrentLocation] = useState<LocationResult | string | undefined>();
  const [currentOptions, setCurrentOptions] = useState<{
    limit?: number;
    priceMin?: number;
    priceMax?: number;
    sources?: string[];
    pickupDistance?: number;
    useAi?: boolean;
    enableQueryFilter?: boolean;
    minRelevanceScore?: number;
  }>({});

  const search = useCallback(
    async (
      query: string,
      location?: LocationResult | string,
      options?: {
        limit?: number;
        priceMin?: number;
        priceMax?: number;
        sources?: string[];
        pickupDistance?: number;
        useAi?: boolean;
        enableQueryFilter?: boolean;
        minRelevanceScore?: number;
      }
    ) => {
      if (!query.trim()) {
        setError('Query is required');
        return;
      }

      // Reset pagination for new searches
      setCurrentOffset(prevOffset => {
        const isNewSearch = currentQuery !== query || currentLocation !== location;
        if (isNewSearch) {
          setListings([]);
          return 0;
        }
        return prevOffset;
      });
      
      const isNewSearch = currentQuery !== query || currentLocation !== location;
      setLoading(isNewSearch);
      setLoadingMore(!isNewSearch);
      setError(null);

      try {
        const result = await searchProducts({
          query,
          location,
          ...options,
          offset: isNewSearch ? 0 : currentOffset,
          userId: userIdOrUndefined,
          userEmail,
        });

        const allListings = result?.listings || [];
        
        // Apply query relevance filtering if enabled
        const enableFilter = options?.enableQueryFilter !== false; // Default to true
        const minScore = options?.minRelevanceScore ?? 0.3;
        
        let finalListings: ProductListing[];
        
        if (enableFilter) {
          // Create filter with user's preferred minimum score
          const filter = new QueryRelevanceFilter(minScore);
          const scoredListings = filter.filterAndScore(allListings, query);
          
          // Sort by relevance score (higher first), then by price (lower first) as tiebreaker
          scoredListings.sort((a, b) => {
            // Primary sort: relevance score (descending)
            if (b.score !== a.score) {
              return b.score - a.score;
            }
            // Secondary sort: price (ascending)
            const priceA = a.listing.price ?? Infinity;
            const priceB = b.listing.price ?? Infinity;
            return priceA - priceB;
          });
          
          finalListings = scoredListings.map(({ listing }) => listing);
        } else {
          // No filtering, just sort by price
          finalListings = [...allListings].sort((a, b) => {
            const priceA = a.price ?? Infinity;
            const priceB = b.price ?? Infinity;
            return priceA - priceB;
          });
        }
        
        // Append new listings for pagination, or replace for new searches
        if (isNewSearch) {
          setListings(finalListings);
          setCurrentQuery(query);
          setCurrentLocation(location);
          setCurrentOptions(options || {});
        } else {
          setListings(prev => [...prev, ...finalListings]);
        }
        
        setHasMore(result?.has_more ?? false);
        if (isNewSearch) {
          setCurrentOffset(finalListings.length);
        } else {
          setCurrentOffset(prev => prev + finalListings.length);
        }
        
        // Convert location to string for history storage
        if (isNewSearch) {
          const locationString = typeof location === 'object' ? location.name : location;
          await addSearchHistory(query, locationString);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to search products');
        if (isNewSearch) {
          setListings([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userIdOrUndefined, userEmail]
    // Note: We intentionally don't include currentQuery, currentLocation, currentOffset in deps
    // because we use them to detect new searches vs pagination
  );

  const searchLocations = useCallback(async (locationQuery: string) => {
    if (!locationQuery.trim()) {
      setLocations([]);
      return;
    }

    // Don't search if query is too short (less than 2 characters)
    if (locationQuery.trim().length < 2) {
      setLocations([]);
      return;
    }

    setLoadingLocations(true);
    try {
      const results = await getLocations(locationQuery.trim(), userIdOrUndefined, userEmail);
      // Ensure we always have an array, even if API returns unexpected format
      const locationsArray = Array.isArray(results?.locations) ? results.locations : [];
      setLocations(locationsArray);
    } catch (err) {
      console.error('Failed to search locations:', err);
      // On error, clear locations but don't show error to user (they'll see "No locations found")
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, [userIdOrUndefined, userEmail]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading || !currentQuery) {
      return;
    }

    setLoadingMore(true);
    setError(null);

    try {
      const result = await searchProducts({
        query: currentQuery,
        location: currentLocation,
        ...currentOptions,
        offset: currentOffset,
        userId: userIdOrUndefined,
        userEmail,
      });

      const allListings = result?.listings || [];
      
      // Apply query relevance filtering if enabled
      const enableFilter = currentOptions?.enableQueryFilter !== false;
      const minScore = currentOptions?.minRelevanceScore ?? 0.3;
      
      let finalListings: ProductListing[];
      
      if (enableFilter) {
        const filter = new QueryRelevanceFilter(minScore);
        const scoredListings = filter.filterAndScore(allListings, currentQuery);
        
        scoredListings.sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          const priceA = a.listing.price ?? Infinity;
          const priceB = b.listing.price ?? Infinity;
          return priceA - priceB;
        });
        
        finalListings = scoredListings.map(({ listing }) => listing);
      } else {
        finalListings = [...allListings].sort((a, b) => {
          const priceA = a.price ?? Infinity;
          const priceB = b.price ?? Infinity;
          return priceA - priceB;
        });
      }
      
      setListings(prev => [...prev, ...finalListings]);
      setHasMore(result?.has_more ?? false);
      setCurrentOffset(prev => prev + finalListings.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more products');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loading, currentQuery, currentLocation, currentOptions, currentOffset, userIdOrUndefined, userEmail]);

  return {
    listings,
    loading,
    loadingMore,
    error,
    search,
    loadMore,
    hasMore,
    locations,
    loadingLocations,
    searchLocations,
  };
}